import type { RunResearchWorkflowRequest, RunResearchWorkflowResponse } from '~/features/worldcup-research/types'
import { Button, Card } from '@heroui/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useSearch } from '@tanstack/react-router'
import * as React from 'react'
import { useTranslations } from 'use-intl'
import { runResearchWorkflow } from '~/features/worldcup-research/api'
import { createSessionQuery } from '~/lib/session-query'
import {
  compactJson,
  EvidenceList,
  formatValue,
  isEvidenceTableRow,
  KeyValue,
  PageHeader,
  SelectInput,
  StateText,
  TextInput,
  userVisibleWarnings,
} from './components'

export function EvidenceExplorer() {
  const t = useTranslations('ResearchWorkspace.evidence')
  const search = useSearch({ strict: false }) as Partial<{
    intent: RunResearchWorkflowRequest['intent']
    team: string
    secondTeam: string
    opponent: string
    competitionCode: NonNullable<RunResearchWorkflowRequest['scope']['competitionCode']>
    competitionName: string
    season: string | number
  }>
  const inferredIntent: RunResearchWorkflowRequest['intent'] = search.intent
    ?? (search.secondTeam ? 'team_comparison' : search.opponent ? 'team_match_search' : 'team_summary')
  const [intent, setIntent] = React.useState<RunResearchWorkflowRequest['intent']>(inferredIntent)
  const [team, setTeam] = React.useState(search.team ?? 'Argentina')
  const [secondTeam, setSecondTeam] = React.useState(search.secondTeam ?? 'France')
  const [opponent, setOpponent] = React.useState(search.opponent ?? '')
  const [competitionName, setCompetitionName] = React.useState(search.competitionName ?? 'FIFA World Cup')
  const [competitionCode, setCompetitionCode] = React.useState<NonNullable<RunResearchWorkflowRequest['scope']['competitionCode']>>(search.competitionCode ?? 'world_cup')
  const [season, setSeason] = React.useState(search.season ? String(search.season) : '2022')
  const workflowMutation = useMutation({
    mutationFn: (request: RunResearchWorkflowRequest) => runResearchWorkflow(request),
  })
  const resultRef = React.useRef<HTMLDivElement>(null)
  const hasContext = Boolean(search.team || search.secondTeam || search.opponent || search.competitionCode || search.competitionName || search.season)
  const intentOptions = [
    { value: 'team_summary', label: t('intentOptions.teamSummary') },
    { value: 'team_comparison', label: t('intentOptions.teamComparison') },
    { value: 'team_match_search', label: t('intentOptions.teamMatchSearch') },
  ]
  const competitionOptions = [
    { value: 'world_cup', label: t('competitionOptions.worldCup') },
    { value: 'euro', label: t('competitionOptions.euro') },
    { value: 'copa_america', label: t('competitionOptions.copaAmerica') },
    { value: 'nations_league', label: t('competitionOptions.nationsLeague') },
    { value: 'world_cup_qualifier', label: t('competitionOptions.worldCupQualifier') },
    { value: 'friendlies', label: t('competitionOptions.friendlies') },
  ]
  const selectedIntentLabel = intentOptions.find(option => option.value === intent)?.label ?? intent
  const selectedCompetitionLabel = competitionOptions.find(option => option.value === competitionCode)?.label ?? competitionCode
  const summaryItems = [
    { key: 'target', label: t('summary.target'), value: selectedIntentLabel },
    { key: 'team', label: t('teamLabel'), value: team },
    ...(intent === 'team_comparison' ? [{ key: 'secondTeam', label: t('secondTeamLabel'), value: secondTeam }] : []),
    ...(intent === 'team_match_search' ? [{ key: 'opponent', label: t('opponentLabel'), value: opponent }] : []),
    { key: 'competition', label: t('competitionCodeLabel'), value: selectedCompetitionLabel },
    { key: 'competitionName', label: t('competitionNameLabel'), value: competitionName },
    { key: 'season', label: t('seasonLabel'), value: season },
  ]

  React.useEffect(() => {
    if (workflowMutation.data)
      resultRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }, [workflowMutation.data])

  function submitEvidence(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const scope: RunResearchWorkflowRequest['scope'] = {
      competitionCode,
      competitionName: competitionName.trim() || undefined,
      season: season ? Number(season) as RunResearchWorkflowRequest['scope']['season'] : undefined,
      researchView: competitionCode === 'world_cup_qualifier' ? 'competitive_context' : 'major_tournament',
      minDataQualityScore: 0.65,
    }
    const request: RunResearchWorkflowRequest = {
      intent,
      scope,
      team: team.trim() || undefined,
      teams: intent === 'team_comparison' ? [team.trim(), secondTeam.trim()] as [string, string] : undefined,
      opponent: opponent.trim() || undefined,
      limit: 25,
      offset: 0,
      sortDirection: 'asc',
    }
    workflowMutation.mutate(request)
  }

  return (
    <div className="space-y-5 pb-12">
      <PageHeader eyebrow={t('eyebrow')} title={t('title')} subtitle={t('subtitle')} />
      {hasContext && (
        <Card className="border-primary/30 bg-primary/5 p-4" variant="secondary">
          <div className="grid gap-4 lg:grid-cols-[1.15fr_0.85fr]">
            <div>
              <div className="text-sm font-bold">{t('contextTitle')}</div>
              <p className="mt-1 text-sm text-muted">{t('contextBody')}</p>
              <dl className="mt-4 grid gap-2 sm:grid-cols-2">
                {summaryItems.map(item => (
                  <div key={item.key} className="rounded-md border border-divider bg-background/70 p-3">
                    <dt className="text-xs font-medium text-muted">{item.label}</dt>
                    <dd className="mt-1 text-sm font-semibold">{item.value || t('summary.empty')}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className="rounded-md border border-divider bg-background/70 p-4">
              <div className="text-sm font-bold">{t('resultPreviewTitle')}</div>
              <p className="mt-2 text-sm text-muted">{t('resultPreviewBody')}</p>
            </div>
          </div>
        </Card>
      )}
      <Card className="p-5" variant="secondary">
        <Card.Header>
          <div>
            <h2 className="text-lg font-bold">{t('scopeTitle')}</h2>
            <p className="text-sm text-muted">{t('scopeSubtitle')}</p>
          </div>
        </Card.Header>
        <form className="grid gap-3 lg:grid-cols-3" onSubmit={submitEvidence}>
          <SelectInput label={t('intentLabel')} value={intent} options={intentOptions} onChange={value => setIntent(value as RunResearchWorkflowRequest['intent'])} />
          <TextInput label={t('teamLabel')} value={team} onChange={setTeam} />
          {intent === 'team_comparison' && <TextInput label={t('secondTeamLabel')} value={secondTeam} onChange={setSecondTeam} />}
          {intent === 'team_match_search' && <TextInput label={t('opponentLabel')} value={opponent} onChange={setOpponent} />}
          <SelectInput label={t('competitionCodeLabel')} value={competitionCode} options={competitionOptions} onChange={value => setCompetitionCode(value as typeof competitionCode)} />
          <TextInput label={t('competitionNameLabel')} value={competitionName} onChange={setCompetitionName} />
          <TextInput label={t('seasonLabel')} value={season} onChange={setSeason} inputMode="numeric" />
          <div className="flex items-end">
            <Button type="submit" className="w-full lg:w-auto" variant="primary" isDisabled={workflowMutation.isPending}>
              {workflowMutation.isPending ? t('running') : t('run')}
            </Button>
          </div>
        </form>
      </Card>
      {workflowMutation.isError && <Card className="p-5 text-sm text-danger" variant="secondary">{t('error')}</Card>}
      {workflowMutation.data && (
        <div ref={resultRef}>
          <EvidenceResult result={workflowMutation.data} />
        </div>
      )}
    </div>
  )
}

function EvidenceResult({ result }: { result: RunResearchWorkflowResponse }) {
  const { data: session } = useQuery(createSessionQuery())
  const isAdmin = session?.user?.role === 'admin'
  const t = useTranslations('ResearchWorkspace.evidence')
  const evidence = result.evidencePackage
  const tableRows = (evidence?.rows ?? []).filter(isEvidenceTableRow)
  return (
    <div className="grid gap-5 lg:grid-cols-[0.75fr_1.25fr]">
      <Card className="p-5" variant="secondary">
        <Card.Header>
          <h2 className="text-lg font-bold">{t('packageTitle')}</h2>
        </Card.Header>
        <Card.Content className="space-y-4">
          <KeyValue label={t('status')} value={result.status} />
          <KeyValue label={t('analysisTool')} value={result.analysisTool ?? '-'} />
          {result.reason && <KeyValue label={t('reason')} value={result.reason} />}
          <EvidenceList title={t('warnings')} values={userVisibleWarnings(result.warnings)} />
          <EvidenceList title={t('selected')} values={Object.entries(result.selected).map(([key, value]) => `${key}: ${formatValue(value)}`)} />
          {evidence?.sourceUsage && <EvidenceList title={t('sourceUsage')} values={Object.entries(evidence.sourceUsage).map(([key, value]) => `${key}: ${formatValue(Array.isArray(value) ? value.join(', ') : value)}`)} />}
        </Card.Content>
      </Card>
      <Card className="p-5" variant="secondary">
        <Card.Header>
          <h2 className="text-lg font-bold">{t('evidenceTitle')}</h2>
        </Card.Header>
        <Card.Content className="space-y-4">
          {!evidence && <StateText>{t('skippedBody')}</StateText>}
          {evidence?.allowedUse && <EvidenceList title={t('allowedUse')} values={evidence.allowedUse} />}
          {evidence?.prohibitedUse && <EvidenceList title={t('prohibitedUse')} values={evidence.prohibitedUse} />}
          {tableRows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-left text-sm">
                <thead className="border-b border-divider text-xs uppercase text-muted">
                  <tr>
                    <th className="py-2">{t('table.team')}</th>
                    <th className="py-2">{t('table.opponent')}</th>
                    <th className="py-2">{t('table.competition')}</th>
                    <th className="py-2">{t('table.score')}</th>
                    <th className="py-2">{t('table.stats')}</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.slice(0, 10).map((row: any) => (
                    <tr key={row.id} className="border-b border-divider">
                      <td className="py-2 font-semibold">{row.team}</td>
                      <td className="py-2">{row.opponent}</td>
                      <td className="py-2">
                        {row.competitionName}
                        {' '}
                        {row.season}
                      </td>
                      <td className="py-2">
                        {row.goals}
                        -
                        {row.opponentGoals}
                      </td>
                      <td className="py-2">{t('table.statLine', { shots: formatValue(row.shotsOnTarget), corners: formatValue(row.corners) })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {isAdmin && (
            <details className="rounded-md border border-divider p-3">
              <summary className="cursor-pointer text-sm font-semibold">{t('rawJson')}</summary>
              <pre className="mt-3 max-h-[420px] overflow-auto rounded-md bg-default-100 p-3 text-xs">{compactJson(result)}</pre>
            </details>
          )}
        </Card.Content>
      </Card>
    </div>
  )
}
